// btc15/useLiveBtc.js — Bitcoin's tape, as live as a browser can honestly be.
//
// Coinbase's public WebSocket pushes one ticker frame per trade (BTC-USD
// prints roughly 10-40 times a second). Those frames land in a ref the
// moment they arrive, and state is published on requestAnimationFrame —
// the display's own refresh, ~16.7 ms — and ONLY when the price actually
// changed. So a new price is on screen in the first frame after it exists,
// and a quiet tape costs nothing.
//
// Why not a 1 ms interval (the user's ask, 2026-09-16): a 60 Hz screen
// repaints every 16.7 ms, so 15 of every 16 updates would be computed and
// discarded before a pixel moved, and Coinbase has no new price to give
// between trades anyway — it would be ~975 re-renders a second of an
// unchanged number. rAF IS the ceiling; below it there is nothing to show.
//
// `tps` counts frames actually received over the last second, so the tab
// can show how live it really is rather than claiming it.
import { useEffect, useRef, useState } from "react";

const FEED = "wss://ws-feed.exchange.coinbase.com";

export default function useLiveBtc({ enabled = true, product = "BTC-USD" } = {}) {
  const [state, setState] = useState({ price: null, at: null, connected: false, tps: 0, trades: 0 });
  const latest = useRef(null);
  const stamps = useRef([]);
  const lastPublished = useRef(null);

  useEffect(() => {
    if (!enabled || typeof WebSocket === "undefined" || process.env.NODE_ENV === "test") return undefined;
    let ws, raf, retry, alive = true, backoff = 1000;

    const connect = () => {
      try { ws = new WebSocket(FEED); } catch { return; }
      ws.onopen = () => {
        backoff = 1000;
        ws.send(JSON.stringify({ type: "subscribe", product_ids: [product], channels: ["ticker"] }));
        setState((s) => ({ ...s, connected: true }));
      };
      ws.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (m.type !== "ticker" || !m.price) return;
          latest.current = { price: Number(m.price), at: m.time, side: m.side, size: Number(m.last_size) || 0 };
          stamps.current.push(Date.now());
        } catch {}
      };
      ws.onclose = () => {
        if (!alive) return;
        setState((s) => ({ ...s, connected: false }));
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 15000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    // one publish per animation frame, and only when something changed —
    // a still tape re-renders nothing at all
    const lastTps = { current: -1 };
    const pump = () => {
      raf = requestAnimationFrame(pump);
      const now = Date.now();
      if (stamps.current.length) {
        const cut = now - 1000;
        while (stamps.current.length && stamps.current[0] < cut) stamps.current.shift();
      }
      const l = latest.current;
      const tps = stamps.current.length;
      const priceChanged = l && l.price !== lastPublished.current;
      if (!priceChanged && tps === lastTps.current) return;
      lastTps.current = tps;
      if (priceChanged) lastPublished.current = l.price;
      setState((s) => ({
        ...s,
        price: l ? l.price : s.price,
        at: l ? l.at : s.at,
        side: l ? l.side : s.side,
        size: l ? l.size : s.size,
        trades: priceChanged ? s.trades + 1 : s.trades,
        tps,
      }));
    };
    raf = requestAnimationFrame(pump);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      clearTimeout(retry);
      try { ws && ws.close(); } catch {}
    };
  }, [enabled, product]);

  return state;
}
